import { useImperativeHandle } from 'react';
import { cx } from 'class-variance-authority';
import * as v from 'valibot';
import { useForm, useStore } from '@tanstack/react-form';

import { Button, InputField, SelectField } from '@nvision/ui';

import { useCountries, useStates } from '../../../hooks';
import { type Country, type PostalSearchFormValues, postalSearchFormSchema, type State } from '../../../models';

import styles from './postal-search-form.module.css';

export type SearchFormRef = {
  submit: () => void;
  canSubmit: () => void;
  reset: () => void;
};

interface PostalSearchFormProps {
  defaultValues?: PostalSearchFormValues;
  formRef: React.RefObject<SearchFormRef>;
  handleSubmit: (values: PostalSearchFormValues) => void;
  handleReset: () => void;
}

const getDefaultSearchFormValues = (): PostalSearchFormValues =>
  v.getDefaults(postalSearchFormSchema) as unknown as PostalSearchFormValues;

export const PostalSearchForm: React.FC<PostalSearchFormProps> = ({
  defaultValues,
  formRef,
  handleSubmit,
  handleReset,
}) => {
  const form = useForm({
    defaultValues: defaultValues || getDefaultSearchFormValues(),
    onSubmit: async (props) => {
      handleSubmit(props.value);
    },
    validators: {
      onChange: postalSearchFormSchema,
      onSubmit: postalSearchFormSchema,
    },
  });

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: () => {
      form.reset(getDefaultSearchFormValues());
      handleReset();
    },
    submit: () => {
      form.validate('submit');
      form.handleSubmit();
    },
  }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const { data: countries, isLoading: countriesLoading } = useCountries();
  const country = useStore(form.store, (store) => store.values.country);
  const { data: states, isLoading: statesLoading } = useStates({
    CountryCode: country?.id ?? '',
  });

  const errorMap = useStore(form.store, (store) => store.errorMap.onSubmit);

  const handleCountryChange = (
    nextCountry: Country | null,
    onChange: (value: { id: string; label: string }) => void,
  ) => {
    if (nextCountry) {
      onChange({ id: String(nextCountry.id), label: nextCountry.label });
      form.setFieldValue('additional.state', undefined, { dontValidate: true });
    }
  };

  return (
    <form onSubmit={onSubmit} className={styles.form}>
      <div className={styles.fields}>
        <form.Field name="country">
          {(field) => {
            return (
              <SelectField
                className={styles.field}
                isRequired
                value={field.state.value?.id ? (field.state.value as unknown as Country) : null}
                label="Country"
                options={countries?.list || []}
                placeholder="Select"
                size="sm"
                onBlur={field.handleBlur}
                onSelectionChange={(c) => handleCountryChange(c, field.handleChange)}
                errorMessage={errorMap?.['country.id']?.map((e) => e?.message).join('.')}
                isLoading={countriesLoading}
              />
            );
          }}
        </form.Field>
        <form.Field name="additional">
          {(fields) => (
            <div className={cx(styles.fields, styles.additional)}>
              <form.Field name="additional.city">
                {(field) => (
                  <InputField
                    className={styles.field}
                    value={field.state.value ?? ''}
                    clearable
                    onChange={(e) => field.handleChange(e.target.value)}
                    label="City"
                    placeholder="Enter"
                    size="sm"
                    errorMessage={fields.state.meta.errorMap.onChange?.map((e) => e.message).join('. ')}
                  />
                )}
              </form.Field>
              <form.Field name="additional.state">
                {(field) => (
                  <SelectField
                    className={styles.field}
                    value={(field.state.value as unknown as State | undefined) ?? null}
                    label="State/Province"
                    options={states?.list || []}
                    placeholder="Select"
                    size="sm"
                    onBlur={field.handleBlur}
                    onSelectionChange={(state) =>
                      state && field.handleChange({ id: String(state.id), label: state.label })
                    }
                    errorMessage={fields.state.meta.errorMap.onChange?.map((e) => e.message).join('. ')}
                    isLoading={statesLoading}
                  />
                )}
              </form.Field>
              <form.Field name="additional.portPostal">
                {(field) => (
                  <InputField
                    className={styles.field}
                    value={field.state.value ?? ''}
                    clearable
                    onChange={(e) => field.handleChange(e.target.value)}
                    label="Port Postal"
                    placeholder="Enter"
                    size="sm"
                    errorMessage={fields.state.meta.errorMap.onChange?.map((e) => e.message).join('. ')}
                  />
                )}
              </form.Field>
            </div>
          )}
        </form.Field>
      </div>
      <Button type="submit">Search</Button>
      <Button outline secondary onClick={() => formRef.current?.reset()}>
        Reset
      </Button>
    </form>
  );
};
